import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowDropZoneParams,
  SelectionChangedEvent,
  ValueGetterParams
} from 'ag-grid-community';
import {IPaginationData} from '../../../shared-models/pagination/pagination-data';
import {
  gridColumnTypes
} from '../../../../shared-utilities/models-old/ngp-report-grid/defaults/column-types/column-types';
import {Store} from '@ngrx/store';
import {
  generalColDefDefaults
} from '../../../../shared-utilities/models-old/ngp-report-grid/defaults/general/general-col-def-defaults';
import {ISupplier, ISupplierScheduled} from '../../../shared-models/stock/suppliers';
import {fuzzyFilterArrayOfObjects} from '../../../shared-utils/advanced filters/search';
import {getDeepCopyOfObject} from '../../../shared-utils/object/object.utils';
import {SharedModalBasicComponent} from '../shared-modal-basic/shared-modal-basic.component';
import {ISharedModalBasic} from '../../../shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {getTypesenseFacetCounts} from '../../../../features/auto-ordering/store/auto-ordering.actions';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {ISelectOption} from '../../../shared-models/custom-select/custom-select';
import {
  SharedCustomSelectComponent
} from '../../../shared-module-ui/components/shared-custom-select/components/shared-custom-select/shared-custom-select.component';
import {LocalStorageKeysEnum} from '../../../../features/core/models';
import {LocalStorageService} from '../../../../features/core/service';
import {
  SharedVisibleFieldsComponent
} from '../../../shared-module-ui/components/shared-visible-fields/shared-visible-fields.component';
import {SharedGridLoadingOverlayComponent} from '../shared-grid-loading-overlay/shared-grid-loading-overlay.component';
import {IAutoOrderAccountSettings, TLocaleDateFormat} from '../../../../features/settings-new/models';
import {selectApplicationPreferenceSettings} from '../../../../features/settings-new/store/setting.selectors';
import {decodeOnHoldCode} from '../../../shared-module-ui/utils/on-hold-code/on-hold-code.utils';

@Component({
  selector: 'app-shared-supplier-search',
  templateUrl: './shared-supplier-search.component.html',
  styleUrls: ['./shared-supplier-search.component.scss'],
  standalone: false,
})
export class SharedSupplierSearchComponent implements AfterViewInit {

  @ViewChild('visibleFieldsSelect') visibleFieldsSelect: SharedCustomSelectComponent;
  @ViewChild('visibleFields') visibleFieldsComponent: SharedVisibleFieldsComponent;

  @Output() readonly stockItemsSelected = new EventEmitter<ISupplierScheduled[]>();
  @Output() readonly rowDragAndDropped = new EventEmitter<ISupplierScheduled[]>();
  @Output() readonly emitSelectedValues = new EventEmitter<ISupplierScheduled>();
  @Output() readonly emitCloseSupplierSearch = new EventEmitter<void>();

  @Input() title = 'Select Suppliers';
  @Input() overlayTemplate = 'No Supplier Found';
  @Input() multiple = false;
  @Input() selectAll = false;
  @Input() isModal = true;
  @Input() allowDrag = false;
  @Input() acceptButtonText: string[] = [];
  @Input() limit = 100;
  @Input() checkForDuplicates = false;
  @Input() isEditSchedule = false;

  #facetCounts = {};

  @Input() suppliers: Record<string, ISupplier> = {};
  @Input() suppAssignments: Record<string, Record<string, IAutoOrderAccountSettings>> = {};
  @Input() saving = false;


  selectedSuppliers: ISupplierScheduled[] = [];
  paginationData: IPaginationData;
  searchString = '';
  gridApi: GridApi;
  totalItems: number;
  selectedStockItemsMap: Map<string, ISupplierScheduled> = new Map<string, ISupplierScheduled>();
  preview = false;
  fuzzySuppliers: ISupplierScheduled[];

  suppDisMod = {
    sort: {by: 'account', asc: true},
    search: {substr: '', in: ['name', 'account']},
    filter: {assigned: true, unassigned: true},
  };
  dontSowDisabled = true;
  hideNoItems = true;

  visibleFieldsOptions: Record<string, ISelectOption> = {};
  selectedVisibleFields: string[] = [];
  protected readonly localStorageEnum = LocalStorageKeysEnum;
  protected readonly columnTypes = gridColumnTypes;
  protected readonly generalColDefDefaults = generalColDefDefaults;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons: typeof IconsEnum = IconsEnum;
  #gridColDefs: ColDef[];
  #gridOptions: GridOptions;
  showFilterMenu = false;
  defaultColDefs: ColDef[];
  private dateSettings: TLocaleDateFormat;

  constructor(
    private readonly store: Store,
    private modalController: ModalControllerService,
    private localStorageService: LocalStorageService,
    private cdr: ChangeDetectorRef,
  ) {

  }


  get facetCounts(): Record<string, Record<string, number>> {
    return this.#facetCounts;
  }

  get selectedStockItemCount(): number {
    return this.selectedStockItemsMap.size;
  }

  get getPreviewButtonData(): string {
    if (this.preview) {
      return `View All Suppliers`;
    } else {
      return this.selectedStockItemCount !== 1 ? `View Selected Suppliers` : `View Selected Supplier`;
    }
  }

  get gridColDefs(): ColDef[] {
    return this.#gridColDefs;
  }

  get getGridOptions(): GridOptions {
    return this.#gridOptions;
  }

  get getDateSettings(): TLocaleDateFormat {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.dateSettings;
  }

  @Input() set setStockData(suppliers: Record<string, ISupplierScheduled>) {
    if (suppliers) {
      this.searchString = '';
      this.selectedSuppliers = Object.values(suppliers) ?? [];
      this.fuzzySuppliers = fuzzyFilterArrayOfObjects<ISupplierScheduled>(getDeepCopyOfObject(this.selectedSuppliers), this.searchString, ['supplierId', 'name']);
    }
  }

  @Input() set facetCounts(value: Record<string, Record<string, number>>) {
    this.#facetCounts = value;
    if (this.gridApi) {
      this.gridApi.refreshCells({force: true});
    }
  }

  @Input() set gridColDefs(colDefs: ColDef[]) {
    this.#gridColDefs = [...colDefs];

    const itemCountCol = this.#gridColDefs.find(col => col.field === 'itemCount');
    if (itemCountCol) {
      itemCountCol.valueGetter = (params: ValueGetterParams): number => {
        if (!params.data || !this.facetCounts || !this.facetCounts['regularSuppCode']) {
          return 0;
        }
        const supplierId = params.data.account || params.data.supplierId;
        return this.facetCounts['regularSuppCode'][supplierId] || 0;
      };

      itemCountCol.cellRenderer = (params: ICellRendererParams): string | number | null => {
        return params.getValue();
      };
    }

    this.setLocalStorageColDefs();
  }

  @Input() set setDefaultColDefs(colDefs: ColDef[]) {
    this.defaultColDefs = [...colDefs];
  }


  @Input() set setSelectedSupplier(supplier: { id: string, select: boolean }) {
    if (!this.gridApi) {
      return;
    }
    if (!supplier.id) {
      return;
    }
    this.gridApi.forEachNode((node) => {
      if (node.data && node.data.supplierId === supplier.id) {
        node.setSelected(supplier.select);
        if (!supplier.select) {
          this.selectedStockItemsMap.delete(supplier.id);
        }
      }
    });
  }

  @Input() set gridOptions(gridOptions: GridOptions) {
    this.#gridOptions = {
      ...gridOptions,
      loadingOverlayComponent: SharedGridLoadingOverlayComponent,
      context: {store: this.store, dateSettings: this.getDateSettings},
    };
  }

  ngAfterViewInit(): void {
    this.setLocalStorageColDefs();
  }

  setLocalStorageColDefs(): void {
    if (this.isEditSchedule) {
      try {
        const order = this.localStorageService.getLocalStorageItem(this.localStorageEnum.agGridColDefOrderAddSupplier);
        if (order && Array.isArray(order) && this.gridColDefs) {
          this.reorderColumnDefinitions(order as string[]);
        } else if (this.gridColDefs && this.gridApi) {
          // No local storage - initialize grid with default column definitions
          // This ensures the grid properly renders with the default visibility settings
          this.gridApi.setGridOption('columnDefs', this.gridColDefs);

          // Initialize visible fields component with default column definitions
          if (this.visibleFieldsComponent) {
            setTimeout(() => {
              this.visibleFieldsComponent.setGridColDefs = [...this.gridColDefs];
            }, 0);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
    this.gridApi.setGridOption('onRowDataUpdated', () => {
      this.restoreSelectedRows();
    });
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      if (event.source !== 'rowDataChanged') {
        this.updateSelectedSupplier(event);
      }
    });
    if (this.allowDrag) {
      this.onDragAndDropSupplier();
    }
    this.store.dispatch(getTypesenseFacetCounts());
    // Please unsubscribe
    this.store.select(selectApplicationPreferenceSettings)
      .subscribe((preferences: { dateFormat: TLocaleDateFormat }) => {
        this.dateSettings = preferences?.dateFormat;
      });
    this.setLocalStorageColDefs();

  }

  onGridSizeChange(): void {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  onDragAndDropSupplier(): void {

    const container = document.querySelector(`.days`);
    if (!container) {
      return;
    }
    const dropZone: RowDropZoneParams = {
      getContainer: () => container as HTMLElement,
      onDragEnter: () => {
        container.classList.add('dragging');
      },
      onDragLeave: () => {
        container.classList.remove('dragging');
      },
      onDragStop: async dropParams => {
        const draggedRowData: ISupplierScheduled[] = [];
        dropParams.nodes.forEach((node: IRowNode<ISupplierScheduled>) => {
          draggedRowData.push(node.data);
        });
        const selectedItems = Array.from(this.selectedStockItemsMap.values());
        const itemsToEmit = selectedItems.length < 1 ? draggedRowData : selectedItems;
        const onHoldSuppliers = itemsToEmit.filter((item: ISupplierScheduled) => decodeOnHoldCode(item.onHoldCode, {
          returnCodes: false,
          isSupplier: true,
        }).includes('Purchase Orders'));
        const noItemsSuppliers = itemsToEmit.filter((item: ISupplierScheduled) => !this.facetCounts['regularSuppCode'][item.account]);

        if (onHoldSuppliers.length > 0 || noItemsSuppliers.length > 0) {
          const supplierIdArray = [
            ...onHoldSuppliers.map((supplier: ISupplierScheduled) => `${supplier.account} - ${supplier.name}\n`),
            ...noItemsSuppliers.map((supplier: ISupplierScheduled) => `${supplier.account} - ${supplier.name}\n`),
          ];
          const supplierIdSetArray = Array.from(new Set(supplierIdArray));
          const modalProps: ISharedModalBasic = {
            buttonAccept: true,
            buttonAcceptText: 'Confirm Selecting',
            buttonClose: true,
            buttonCloseCross: false,
            buttonCloseText: 'Cancel',
            buttonSecondary: true,
            buttonSecondaryText: 'Proceed Without Shown Suppliers',
            modalTitle: 'Warning: Supplier On Hold/Have no stock Items',
            modalTitleIcon: this.icons.helpCircleOutline,
            modalTitleIconColor: this.iconColourEnum.yellow,
            contextHeading: 'Please select how wish to continue',
            contextDescription: [
              'The Following Suppliers are either on hold or do not have any stock items linked to them',
              ...supplierIdSetArray,
            ],
          };

          const modalController = await this.modalController.create({
            component: SharedModalBasicComponent,
            componentProps: modalProps,
            cssClass: ['shared-basic-modal-css-medium'],
          });

          await modalController.present();
          const {data} = await modalController.onDidDismiss();
          if (data.buttonPressed === 'button-accept') {
            this.rowDragAndDropped.emit(itemsToEmit);
            container.classList.remove('dragging');
            dropParams.nodes.forEach((node: IRowNode<ISupplierScheduled>) => {
              const supplierId = (node.data).supplierId;
              if (this.selectedStockItemsMap.has(supplierId)) {
                node.setSelected(false);
              }
            });
            this.selectedStockItemsMap.clear();
            this.preview = false;
            this.searchString = '';
            this.gridApi.setGridOption('rowData', this.selectedSuppliers);
          } else if (data.buttonPressed === 'button-secondary') {
            const suppliersToRemove = new Set([...onHoldSuppliers, ...noItemsSuppliers]);

            const filteredItems = itemsToEmit.filter(
              (supplier: ISupplierScheduled) => !suppliersToRemove.has(supplier),
            );
            this.rowDragAndDropped.emit(filteredItems);
            container.classList.remove('dragging');
            dropParams.nodes.forEach((node: IRowNode<ISupplierScheduled>) => {
              const supplierId = (node.data).supplierId;
              if (this.selectedStockItemsMap.has(supplierId)) {
                node.setSelected(false);
              }
            });
            this.selectedStockItemsMap.clear();
            this.preview = false;
            this.searchString = '';
            this.gridApi.setGridOption('rowData', this.selectedSuppliers);
          } else {
            container.classList.remove('dragging');
          }
        } else {
          this.rowDragAndDropped.emit(itemsToEmit);
          container.classList.remove('dragging');
          dropParams.nodes.forEach((node: IRowNode<ISupplierScheduled>) => {
            const supplierId = (node.data).supplierId;
            if (this.selectedStockItemsMap.has(supplierId)) {
              node.setSelected(false);
            }
          });
          this.selectedStockItemsMap.clear();
          this.preview = false;
          this.searchString = '';
          this.gridApi.setGridOption('rowData', this.selectedSuppliers);
        }

      },
    };

    this.gridApi.addRowDropZone(dropZone);
  }

  updateSelectedSupplier(event?: SelectionChangedEvent): void {

    if (this.gridApi !== undefined) {
      if (this.multiple) {
        let selectedNodes;
        try {
          selectedNodes = this.gridApi.getSelectedNodes();
        } catch (error) {
          console.warn('getSelectedNodes not available, skipping selection update:', error);
          return;
        }
        const newlySelectedSuppliers: typeof this.selectedSuppliers = selectedNodes?.map((node: IRowNode<ISupplierScheduled>): ISupplierScheduled => node.data);
        const displayedItems: typeof this.selectedSuppliers = [];
        if (this.checkForDuplicates) {
          newlySelectedSuppliers?.forEach((supplier: ISupplierScheduled) => {
            if (!this.selectedStockItemsMap.has(supplier.supplierId)) {
              this.emitSelectedValues.emit(supplier);
            }
          });
        }
        this.gridApi.forEachNode((node): void => {
          if (node.data) {
            displayedItems.push(node.data as ISupplierScheduled);
          }
        });
        const mergedSelection = new Map<string, ISupplierScheduled>(this.selectedStockItemsMap);
        displayedItems.forEach((item: ISupplierScheduled) => {
          if (mergedSelection.has(item.supplierId) && (event?.source === 'rowClicked' || event?.source === 'uiSelectAll')) {
            mergedSelection.delete(item.supplierId);
          }
        });

        for (const item of newlySelectedSuppliers) {
          mergedSelection.set(item.supplierId, item);
        }
        this.selectedStockItemsMap = mergedSelection;
        this.cdr.detectChanges();
      } else {
        if (event?.source === 'rowClicked' || event?.source === 'uiSelectAll') {
          let selectedNodes;
          try {
            selectedNodes = this.gridApi.getSelectedNodes();
          } catch (error) {
            console.warn('getSelectedNodes not available for single selection:', error);
            return;
          }
          this.selectedStockItemsMap = new Map();
          if (selectedNodes.length > 0) {
            const lastSelectedNode = selectedNodes[selectedNodes.length - 1];
            const selectedSupplier = lastSelectedNode.data as ISupplierScheduled;
            this.selectedStockItemsMap.set(selectedSupplier.supplierId, selectedSupplier);
          }
        }
        this.cdr.detectChanges();
      }
    }
  }

  restoreSelectedRows(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.forEachNode((node: IRowNode<ISupplierScheduled>) => {
      if (this.selectedStockItemsMap.has((node.data)?.supplierId)) {
        node.setSelected(true);
      }
    });
  }

  searchOnKeyUp(searchString: string): void {
    this.searchString = searchString;
    this.gridApi.removeEventListener('selectionChanged', this.updateSelectedSupplier);
    this.gridApi.setGridOption(
      'quickFilterText',
      this.searchString,
    );
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      this.updateSelectedSupplier(event);
    });
    this.restoreSelectedRows();
  }

  onShowFilterMenuClick(event: Event): void {
    event.stopPropagation();
    this.showFilterMenu = true;
  }


  onViewSelectedClick(): void {
    this.gridApi.removeEventListener('selectionChanged', this.updateSelectedSupplier);
    this.preview = !this.preview;
    this.gridApi.setGridOption(
      'quickFilterText',
      '',
    );
    if (this.preview) {
      const selectedArray = Array.from(this.selectedStockItemsMap.values());
      this.gridApi.setGridOption('rowData', selectedArray);
      this.restoreSelectedRows();
    } else {
      this.gridApi.setGridOption('rowData', this.selectedSuppliers);
      this.restoreSelectedRows();
    }
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      this.updateSelectedSupplier(event);
    });
  }

  onGetSelectedStockItemsClick(): void {
    const selectedItems = Array.from(this.selectedStockItemsMap.values());
    this.stockItemsSelected.emit(selectedItems);
    if (!this.isModal && this.isEditSchedule) {
      void this.simulateDragEnd();
    }
  }

  async simulateDragEnd(): Promise<void> {
    const itemsToEmit = Array.from(this.selectedStockItemsMap.values());
    const onHoldSuppliers = itemsToEmit.filter((item: ISupplierScheduled) => decodeOnHoldCode(item.onHoldCode, {
      returnCodes: false,
      isSupplier: true,
    }).includes('Purchase Orders'));
    const noItemsSuppliers = itemsToEmit.filter((item: ISupplierScheduled) => !this.facetCounts['regularSuppCode'][item.account]);

    if (onHoldSuppliers.length > 0 || noItemsSuppliers.length > 0) {
      const supplierIdArray = [
        ...onHoldSuppliers.map((supplier: ISupplierScheduled) => `${supplier.account} - ${supplier.name}\n`),
        ...noItemsSuppliers.map((supplier: ISupplierScheduled) => `${supplier.account} - ${supplier.name}\n`),
      ];
      const supplierIdSetArray = Array.from(new Set(supplierIdArray));
      const modalProps: ISharedModalBasic = {
        buttonAccept: true,
        buttonAcceptText: 'Confirm Selecting',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Cancel',
        buttonSecondary: true,
        buttonSecondaryText: 'Proceed Without Shown Suppliers',
        modalTitle: 'Warning: Supplier On Hold/Have no stock Items',
        modalTitleIcon: this.icons.helpCircleOutline,
        modalTitleIconColor: this.iconColourEnum.yellow,
        contextHeading: 'Please select how wish to continue',
        contextDescription: [
          'The Following Suppliers are either on hold or do not have any stock items linked to them',
          ...supplierIdSetArray,
        ],
      };

      const modalController = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps: modalProps,
        cssClass: ['shared-basic-modal-css-medium'],
      });

      await modalController.present();
      const {data} = await modalController.onDidDismiss();
      if (data.buttonPressed === 'button-accept') {
        this.selectedStockItemsMap.clear();
        this.preview = false;
        this.searchString = '';
        this.gridApi.setGridOption('rowData', this.selectedSuppliers);
        void this.modalController.dismiss(itemsToEmit);
        this.rowDragAndDropped.emit(itemsToEmit);

      } else if (data.buttonPressed === 'button-secondary') {
        const suppliersToRemove = new Set([...onHoldSuppliers, ...noItemsSuppliers]);
        const filteredItems = itemsToEmit.filter(
          (supplier: ISupplierScheduled) => !suppliersToRemove.has(supplier),
        );
        this.selectedStockItemsMap.clear();
        this.preview = false;
        this.searchString = '';
        this.gridApi.setGridOption('rowData', this.selectedSuppliers);
        void this.modalController.dismiss(filteredItems);
        this.rowDragAndDropped.emit(filteredItems);
      }
    } else {
      this.selectedStockItemsMap.clear();
      this.preview = false;
      this.searchString = '';
      this.gridApi.setGridOption('rowData', this.selectedSuppliers);
      void this.modalController.dismiss(itemsToEmit);
      this.rowDragAndDropped.emit(itemsToEmit);

    }

    this.onCancelClick();
  }

  onCancelClick(): void {
    this.emitCloseSupplierSearch.emit();
  }

  onSelectAllSuppliersClick(select: boolean): void {
    if (!this.gridApi) {
      return;
    }
    if (!select) {
      this.gridApi.forEachNode((node) => {
        node.setSelected(true);
        const supplier = node.data as ISupplierScheduled;
        this.selectedStockItemsMap.set(supplier.supplierId, supplier);
      });
    } else {
      this.gridApi.forEachNode((node) => {
        node.setSelected(false);
        const supplier = node.data as ISupplierScheduled;
        this.selectedStockItemsMap.delete(supplier.supplierId);
      });
    }
  }

  /**
   * Filters suppliers based on assigned/unassigned status, on-hold status, and stock items availability.
   */
  filterSupp(): void {
    if (!this.isEditSchedule || !this.suppliers) {
      return;
    }

    const assignedIds = Object.keys(this.suppliers).filter(
      (id: string) => this.suppAssignments[id] && Object.keys(this.suppAssignments[id]).length,
    );
    const unassignedIds = Object.keys(this.suppliers).filter(
      id => !this.suppAssignments[id] || !Object.keys(this.suppAssignments[id]).length,
    );

    let out: Record<string, ISupplier> = {};
    if (this.suppDisMod.filter.assigned) {
      assignedIds.forEach((id: string) => (out[id] = this.suppliers[id]));
    }
    if (this.suppDisMod.filter.unassigned) {
      unassignedIds.forEach((id: string) => (out[id] = this.suppliers[id]));
    }

    if (this.dontSowDisabled) {
      const filteredOut: Record<string, ISupplier> = {};
      for (const id in out) {
        if (
          !decodeOnHoldCode(out[id].onHoldCode, {returnCodes: false, isSupplier: true}) ||
          decodeOnHoldCode(out[id].onHoldCode, {returnCodes: false, isSupplier: true}).length < 1 ||
          !decodeOnHoldCode(out[id].onHoldCode, {returnCodes: false, isSupplier: true}).includes('Purchase Orders')) {
          filteredOut[id] = out[id];
        }
      }
      out = filteredOut;
    }

    if (this.hideNoItems && this.facetCounts?.['regularSuppCode']) {
      const facetSupplierIds = new Set(Object.keys(this.facetCounts['regularSuppCode']));
      const filteredByFacetCounts: Record<string, ISupplier> = {};
      for (const id in out) {
        if (facetSupplierIds.has(id)) {
          filteredByFacetCounts[id] = out[id];
        }
      }
      out = filteredByFacetCounts;
    }

    this.selectedSuppliers = Object.values(out) as ISupplierScheduled[];
    this.fuzzySuppliers = fuzzyFilterArrayOfObjects<ISupplierScheduled>(
      getDeepCopyOfObject(this.selectedSuppliers),
      this.searchString,
      ['supplierId', 'name'],
    );

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.fuzzySuppliers);
    }
  }

  close(): void {
    void this.modalController.dismiss();
  }

  async showSelectionWarning(removedItems: number): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Ok',
      buttonClose: false,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Too Many Items Selected',
      modalTitleIcon: this.icons.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: `Warning: More Than ${this.limit} Items Selected`,
      contextDescription: [
        `${removedItems} items have been deselected.`,
        'To add more items, please do so separately.',
      ],
    };


    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent, componentProps, cssClass: ['shared-basic-modal-css-small'],
    });
    await modalController.present();
  }

  onVisibleFieldsChange(newColDefs: ColDef[]): void {
    if (!this.#gridColDefs || !this.gridApi) {
      return;
    }
    this.gridApi.updateGridOptions({
      columnDefs: newColDefs,
    });
    this.#gridColDefs = newColDefs;
    setTimeout(() => {
      this.gridApi.sizeColumnsToFit();
    }, 0);
  }


  private reorderColumnDefinitions(order: string[]): void {
    if (!this.#gridColDefs?.length || !order?.length) return;

    const orderSet = new Set(order);

    const currentFields = this.#gridColDefs
      .map((c) => c.field)
      .filter((f): f is string => !!f);

    const hasMatchingFields = currentFields.some((f) => orderSet.has(f));

    if (!hasMatchingFields) {
      this.applyColumnDefs(this.#gridColDefs);
      return;
    }

    const updatedColDefs = this.#gridColDefs.map((colDef) => {
      if (colDef.lockVisible) {
        return { ...colDef, hide: false };
      }

      return {
        ...colDef,
        hide: !!colDef.field && !orderSet.has(colDef.field),
      };
    });

    this.#gridColDefs = [
      ...order
        .map((field) => updatedColDefs.find((c) => c.field === field))
        .filter(Boolean),
      ...updatedColDefs.filter((c) => !c.field || !orderSet.has(c.field)),
    ];

    this.applyColumnDefs(this.#gridColDefs);
  }


  private applyColumnDefs(colDefs: ColDef[]): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', colDefs);
    }

    if (this.visibleFieldsComponent) {
      setTimeout(() => {
        this.visibleFieldsComponent.setGridColDefs = [...colDefs];
      });
    }
  }
}
